import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Tooltip, Button, makeStyles, Theme, IconButton } from "@material-ui/core";
import { Cancel, Visibility, EmailOutlined } from "@material-ui/icons";
import { connect } from "react-redux";
import Selector from "../SharedComponents/Selector";
import { Game, GState, Leaderboard } from "../../types/game";
import { Rules, User, UserBrifObj } from "../../types/user";
import ShareGameInformationDialog from "./ShareGameInformationDialog";
import GameActions from "../../actions/games";
import { showAlertDialog } from "../SharedComponents/AlertDialog";
import EmailOptionsDialog from "components/SharedComponents/EmailOptions";
import { isCombinedGame } from "./../../utils/index";
import { ErrorOnlyCallback } from "../../types/actions";
import { EmailTypes } from "types/general";
import { useAdMediaQuery } from "hooks/useAdMediaQuery";
import TournamentTrophyIcon, { TOURNAMENT_TROPHY_COLOR } from "../SharedComponents/TournamentTrophyIcon";

const cn = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(" ");

interface Props {
  game: Game;
  multipleQueryType: boolean;
  queryType: string;
  user: User;
  widescreen: boolean;
  isGameClosed: boolean;
  showTournamentLink?: boolean;
  onTournamentLinkClick?: () => void;
  onQueryTypeChanged(type: string): void;
  onGameClosed(): void;
  closeGame(id: string, callback?: ErrorOnlyCallback): void;
  onRevealButtonClicked(gameId: string, revealPlaceStatus: number | undefined): void;
}

const teamOptions = [
  { value: "all", label: "All" },
  { value: "front", label: "Front" },
  { value: "back", label: "Back" },
];

const ROOT_PAD = 28;
const SEGMENT_W = 168;
const GAP = 6;
const LABELED_BTN_W = 84;
const ICON_BTN_W = 34;
const EXPORT_BTN_W = 34;
const DIVIDER_W = 10;

interface ActionLayoutInput {
  multipleQueryType: boolean;
  hasTournamentAction: boolean;
  showClose: boolean;
  showReveal: boolean;
  showEmail: boolean;
  showExports: boolean;
  hasGameActions: boolean;
}

const measureActionsMinWidth = (layout: ActionLayoutInput, withLabels: boolean) => {
  const btnW = withLabels ? LABELED_BTN_W : ICON_BTN_W;
  let width = ROOT_PAD;

  if (layout.multipleQueryType) {
    width += SEGMENT_W + GAP;
  }
  if (layout.hasTournamentAction) {
    width += btnW + GAP;
  }
  if (layout.showClose) {
    width += btnW + GAP;
  }
  if (layout.showReveal) {
    width += ICON_BTN_W + GAP;
  }
  if (layout.showEmail) {
    width += btnW + GAP;
  }
  if (layout.showExports && layout.hasGameActions) {
    width += DIVIDER_W + GAP;
  }
  if (layout.showExports) {
    width += EXPORT_BTN_W * 3 + GAP * 2;
  }

  return width;
};

const isAllCardsFilled = (game: Game) => {
  let isAlCardsField = true;
  const isCombined = isCombinedGame(game.competition);
  const players = Object.values(game.players).filter(x => x.isTeamCard || !isCombined);
  players.forEach(player => {
    if (Object.values(player.holes).length !== game.competition.holesNumber) {
      isAlCardsField = false;
      return;
    }
  });
  return isAlCardsField;
};

function LeaderboardActions(props: Props) {
  const {
    game,
    queryType,
    user,
    isGameClosed,
    multipleQueryType,
    widescreen,
    showTournamentLink,
    onTournamentLinkClick,
  } = props;
  const userId = user._id;
  const rule = user.rule;
  const classes = useStyles();
  const rootRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [landscape] = useAdMediaQuery();
  const [openDialog, setOpenDialog] = useState(false);

  const canShowRevealButton = (leaderboard?: Leaderboard) => {
    if (!leaderboard || !leaderboard.presentationMode) return false;
    if (leaderboard.revealIndex === undefined || leaderboard.showInTwoSteps) return true;
    return (
      (leaderboard.revealIndex > 0 && leaderboard.reverseHiddenPlace) ||
      (leaderboard.revealIndex < leaderboard.hideUntil && !leaderboard.reverseHiddenPlace)
    );
  };

  const handleCloseGame = (game: Game) => {
    if (isAllCardsFilled(game))
      showAlertDialog("Close game", `You are about to close '${game.name}' game, are you sure?`, () => {
        props.closeGame(game._id, err => !err && props.onGameClosed());
      });
    else
      showAlertDialog(
        "Close game",
        `Some score cards are incomplete. Complete cards or delete players from game.`,
        null,
        null,
      );
  };

  const canManageGame = game.organizers.some((o: UserBrifObj) => o._id === userId);
  const canAccessOrganiserTools = game.organizers.some((o: UserBrifObj) => o._id === userId) || rule === Rules.Admin;
  const hideLiveControls = game.state !== GState.Playing || isGameClosed;
  const { leaderboard } = game;
  const hasTournamentAction = Boolean(showTournamentLink && onTournamentLinkClick);
  const showExports = canAccessOrganiserTools;
  const hasGameActions = hasTournamentAction || canManageGame;
  const showActionCluster = hasGameActions || showExports;
  const showClose = canManageGame && !hideLiveControls;
  const showReveal = canManageGame && canShowRevealButton(leaderboard) && !hideLiveControls;
  const showEmail = canManageGame;
  const hasLabeledActions = hasTournamentAction || showClose || showEmail;

  const actionLayout = useMemo<ActionLayoutInput>(
    () => ({
      multipleQueryType,
      hasTournamentAction,
      showClose,
      showReveal,
      showEmail,
      showExports,
      hasGameActions,
    }),
    [multipleQueryType, hasTournamentAction, showClose, showReveal, showEmail, showExports, hasGameActions],
  );

  const minWidthWithLabels = useMemo(() => measureActionsMinWidth(actionLayout, true), [actionLayout]);
  const showActionLabels = hasLabeledActions && containerWidth > 0 && containerWidth >= minWidthWithLabels;

  useLayoutEffect(() => {
    if (!showActionCluster && !multipleQueryType) return;

    const node = rootRef.current;
    if (!node) return;

    const updateWidth = () => setContainerWidth(node.getBoundingClientRect().width);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [showActionCluster, multipleQueryType]);

  if (widescreen && landscape) return null;
  if (!multipleQueryType && !showActionCluster) return null;

  const labeledBtnClass = showActionLabels ? undefined : classes.iconOnlyAction;

  return (
    <div ref={rootRef} className={classes.root}>
      {multipleQueryType ? (
        <div className={classes.segmentWrap}>
          <Selector
            className={classes.segment}
            onChange={(value: string) => props.onQueryTypeChanged(value)}
            size="small"
            options={teamOptions}
            value={queryType}
            fullWidth
          />
        </div>
      ) : null}

      {showActionCluster ? (
        <div className={classes.actionsWrap} role="toolbar" aria-label="Leaderboard actions">
          {hasGameActions ? (
            <div className={classes.actionGroup}>
              {hasTournamentAction && (
                <Tooltip title="Tournament leaderboards" leaveTouchDelay={2000}>
                  <Button
                    size="small"
                    className={cn(classes.tournamentAction, labeledBtnClass)}
                    onClick={onTournamentLinkClick}
                    startIcon={<TournamentTrophyIcon />}
                    aria-label="Tournament leaderboards"
                  >
                    {showActionLabels ? "Tournament" : null}
                  </Button>
                </Tooltip>
              )}

              {showClose && (
                <Tooltip title="Close game" leaveTouchDelay={2000}>
                  <Button
                    size="small"
                    className={cn(classes.dangerAction, labeledBtnClass)}
                    onClick={() => handleCloseGame(game)}
                    startIcon={<Cancel fontSize="small" />}
                    aria-label="Close game"
                  >
                    {showActionLabels ? "Close" : null}
                  </Button>
                </Tooltip>
              )}

              {showReveal && (
                <Tooltip title="Reveal top players" leaveTouchDelay={2000}>
                  <IconButton
                    size="small"
                    className={classes.iconAction}
                    aria-label="Reveal top players"
                    onClick={() => props.onRevealButtonClicked(game._id, game.leaderboard?.revealPlaceStatus)}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {showEmail && (
                <Tooltip title="Send email" leaveTouchDelay={2000}>
                  <Button
                    size="small"
                    className={cn(classes.emailAction, labeledBtnClass)}
                    onClick={() => setOpenDialog(true)}
                    startIcon={<EmailOutlined fontSize="small" />}
                    aria-label="Send email"
                  >
                    {showActionLabels ? "Email" : null}
                  </Button>
                </Tooltip>
              )}
            </div>
          ) : null}

          {showExports && hasGameActions ? <span className={classes.divider} aria-hidden /> : null}

          {showExports ? (
            <div className={classes.exportGroup}>
              <ShareGameInformationDialog
                game={game}
                gameQueryType={queryType}
                iconButtonClassName={classes.iconAction}
              />
            </div>
          ) : null}

          {canManageGame && (
            <EmailOptionsDialog
              user={user}
              open={openDialog}
              onClose={() => setOpenDialog(false)}
              options={[EmailTypes.FINAL_RESULT, EmailTypes.EMPTY]}
              game={game}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

const actionButtonBase = (theme: Theme) => ({
  minWidth: 32,
  minHeight: 32,
  height: 32,
  padding: theme.spacing(0, 0.75),
  borderRadius: 6,
  textTransform: "none" as const,
  fontSize: "0.75rem",
  fontWeight: 600,
  lineHeight: 1.2,
  "& .MuiButton-label": {
    color: "inherit",
    textTransform: "none",
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.01em",
  },
  "& .MuiButton-startIcon": {
    marginRight: theme.spacing(0.5),
    marginLeft: 0,
    color: "inherit",
  },
});

const useStyles = makeStyles((theme: Theme) => {
  const actionBase = actionButtonBase(theme);

  return {
    root: {
      display: "flex",
      flexWrap: "nowrap",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(0.75, 1.5),
      backgroundColor: "#ffffff",
      boxShadow: "inset 0 -1px 0 rgba(226, 232, 240, 0.92)",
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
      [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(0.75, 2),
        overflowX: "visible",
      },
    },
    segmentWrap: {
      flex: "0 0 auto",
      width: "min(200px, 42vw)",
      minWidth: 0,
    },
    segment: {
      margin: 0,
      width: "100%",
      "& .MuiButtonGroup-root": {
        width: "100%",
        display: "flex",
        boxShadow: "none",
      },
      "& .MuiButtonGroup-grouped": {
        flex: "1 1 0",
        minWidth: 0,
        minHeight: 32,
        padding: theme.spacing(0.5, 0.75),
        fontSize: "0.8125rem",
        fontWeight: 600,
        textTransform: "none",
        letterSpacing: "0.01em",
        borderColor: "rgba(226, 232, 240, 0.95)",
      },
      "& .MuiButton-contained": {
        boxShadow: "none",
      },
      "& .MuiButton-outlined": {
        color: "#64748b",
        backgroundColor: "#f8fafc",
      },
    },
    actionsWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      flex: "1 1 auto",
      minWidth: 0,
      gap: theme.spacing(0.5),
      marginLeft: "auto",
    },
    actionGroup: {
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
      gap: theme.spacing(0.25),
    },
    exportGroup: {
      display: "flex",
      alignItems: "center",
      flexShrink: 0,
      gap: theme.spacing(0.25),
    },
    divider: {
      flexShrink: 0,
      width: 1,
      height: 20,
      margin: theme.spacing(0, 0.25),
      backgroundColor: "rgba(226, 232, 240, 0.95)",
    },
    tournamentAction: {
      ...actionBase,
      color: TOURNAMENT_TROPHY_COLOR,
      "&:hover": {
        backgroundColor: "rgba(254, 251, 235, 0.9)",
      },
    },
    emailAction: {
      ...actionBase,
      color: "#64748b",
      "&:hover": {
        backgroundColor: "rgba(241, 245, 249, 0.95)",
        color: "#334155",
      },
    },
    dangerAction: {
      ...actionBase,
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: "rgba(254, 242, 242, 0.9)",
        color: theme.palette.error.main,
      },
    },
    iconOnlyAction: {
      minWidth: 32,
      maxWidth: 32,
      padding: theme.spacing(0, 0.5),
      "& .MuiButton-startIcon": {
        margin: 0,
      },
    },
    iconAction: {
      width: 32,
      height: 32,
      minWidth: 32,
      minHeight: 32,
      padding: 6,
      borderRadius: 6,
      boxSizing: "border-box",
      color: "#64748b",
      "&:hover": {
        backgroundColor: "rgba(241, 245, 249, 0.95)",
        color: "#334155",
      },
    },
  };
});

export default connect(null, { closeGame: GameActions.closeGame })(LeaderboardActions);
